import { inject, Injectable } from '@angular/core';

import { ChannelGroup, DeviceStatus, DeviceModel } from '@shure/shared/angular/data-access/system-api/models';
import { DeviceBehaviorPluginsService } from '@shure/shared/angular/device-behavior/core';
import { CoverageAudioChannel, CoverageDevice } from '@shure/shared/angular/features/device-coverage/data-access';

import { OnboardingAction, OnboardingStage, OnboardingStep } from '../models';

import { DeviceCoverage3dOnboardingDeviceBehavior } from './coverage-3-d-onboarding.device-behavior';

export interface OnboardingStepConfig {
	name: OnboardingStep;
	actions: OnboardingAction[];
	/**
	 * Applies the step's initial state to a fresh onboarding device, whose lobe channels are all disabled.
	 * Without it, the step starts with the fresh device as is.
	 */
	initDevice?: (device: CoverageDevice) => CoverageDevice;
}

const enableChannels = (device: CoverageDevice, count: number): CoverageDevice => {
	const audioChannels = device.features.audioChannels;
	if (!audioChannels?.value) {
		return device;
	}
	return {
		...device,
		features: {
			...device.features,
			audioChannels: {
				...audioChannels,
				value: audioChannels.value.map((channel, index) => ({ ...channel, enabled: index < count }))
			}
		}
	};
};

@Injectable({ providedIn: 'root' })
export class Coverage3dOnboardingDeviceBehaviorService {
	public readonly plugins = inject(DeviceBehaviorPluginsService);

	public getOnboardingStepsConfigs(): OnboardingStepConfig[] {
		return [
			{
				name: OnboardingStep.SceneClickAndDrag,
				actions: [OnboardingAction.SceneDrag, OnboardingAction.SceneScroll]
			},
			{ name: OnboardingStep.LobeAdd, actions: [OnboardingAction.LobeAdd] },
			{
				name: OnboardingStep.LobeClickAndDrag,
				actions: [OnboardingAction.LobeClickAndDrag],
				initDevice: (device) => enableChannels(device, 1)
			},
			{
				name: OnboardingStep.LobeMultiSelect,
				actions: [OnboardingAction.LobeMultiSelect],
				initDevice: (device) => enableChannels(device, 2)
			},
			{
				name: OnboardingStep.LobeSnap,
				actions: [OnboardingAction.LobeSnap],
				initDevice: (device) => enableChannels(device, 1)
			}
		];
	}

	/**
	 * Creates the onboarding device for a step. Lobe channels are cloned from the real device so they carry all
	 * device-specific features and constraints, but use onboarding-only ids and are never sent to the device API.
	 */
	public createOnboardingDevice(
		model: DeviceModel,
		templateChannels: CoverageAudioChannel[],
		step: OnboardingStepConfig
	): CoverageDevice {
		const channels = templateChannels
			.filter((channel) => channel.group === ChannelGroup.Lobe)
			.map((channel) => ({
				...channel,
				id: `onboarding-${channel.id}`,
				enabled: false,
				device: { id: 'onboarding-device', model, virtual: false }
			}));

		const device: CoverageDevice = {
			id: 'onboarding-device',
			model,
			status: DeviceStatus.Online,
			virtual: false,
			features: channels.length ? { audioChannels: { value: channels, isMissing: false } } : {}
		};
		return step.initDevice?.(device) ?? device;
	}

	public getStageTitle(model: DeviceModel, stage: OnboardingStage): string | undefined {
		return (
			this.plugins.get<DeviceCoverage3dOnboardingDeviceBehavior>(model)?.getOnboardingStageTitle(model, stage) ??
			undefined
		);
	}

	public getStageDescription(model: DeviceModel, stage: OnboardingStage): string | undefined {
		return (
			this.plugins
				.get<DeviceCoverage3dOnboardingDeviceBehavior>(model)
				?.getOnboardingStageDescription(model, stage) ?? undefined
		);
	}

	public getStepTitle(model: DeviceModel, step: OnboardingStep): string | undefined {
		return (
			this.plugins.get<DeviceCoverage3dOnboardingDeviceBehavior>(model)?.getOnboardingStepTitle(model, step) ??
			undefined
		);
	}

	public getStepDescription(model: DeviceModel, step: OnboardingStep): string | undefined {
		return (
			this.plugins
				.get<DeviceCoverage3dOnboardingDeviceBehavior>(model)
				?.getOnboardingStepDescription(model, step) ?? undefined
		);
	}

	public getActionDescription(model: DeviceModel, action: OnboardingAction): string | undefined {
		return (
			this.plugins
				.get<DeviceCoverage3dOnboardingDeviceBehavior>(model)
				?.getOnboardingActionDescription(model, action) ?? undefined
		);
	}
}
